import React, { useMemo, useRef } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import {
  cutCorner,
  ga,
  getEggBalance,
  getStreakCount,
  useIsDesktop,
  useUiLangState,
} from "./GaPrimitives";
import icLock from "../../assets/img/figma/dialogs/ic-lock.svg";
import icComment from "../../assets/img/figma/dialogs/ic-comment.svg";
import icFireTitle from "../../assets/img/1 APP INTRO & FIRST LAUNCH/ic-fire-title.svg";
import icFireSmall from "../../assets/img/1 APP INTRO & FIRST LAUNCH/ic-fire-small.svg";
import claimEgg from "../../assets/img/1 APP INTRO & FIRST LAUNCH/claim-egg-4x.webp";
import eggCollectedArt from "../../assets/img/1 APP INTRO & FIRST LAUNCH/egg-collected-art-4x.webp";
import eggImg from "../../assets/img/2 MAIN SCREENS EN/home/egg-4x.webp";
import fireIcon from "../../assets/img/2 MAIN SCREENS EN/Rewards/ic · fire.png";

export function fillCopy(template, vars = {}) {
  return Object.entries(vars).reduce(
    (text, [key, value]) => text.replaceAll(`{${key}}`, String(value)),
    template || ""
  );
}

export function maskJazzNumber(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  let local = digits;
  if (local.startsWith("92") && local.length > 10) local = `0${local.slice(2)}`;
  if (local.length < 11) return "";
  return `${local.slice(0, 4)} •••• ${local.slice(-3)}`;
}

export function GaCircleClose({ onClick, sx }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label="Close"
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 2,
        width: 30,
        height: 30,
        p: 0,
        borderRadius: "50%",
        border: "1.25px solid rgba(255,255,255,0.95)",
        bgcolor: "transparent",
        color: "#FFFFFF",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 0,
        ...sx,
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 12 12"
        aria-hidden="true"
        sx={{ width: 9, height: 9, display: "block" }}
      >
        <path
          d="M2.15 2.15L9.85 9.85M9.85 2.15L2.15 9.85"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </Box>
    </Box>
  );
}

export function GaDialogShell({
  open,
  onClose,
  children,
  width = 320,
  desktopWidth = 560,
  hideClose = false,
}) {
  const isDesktop = useIsDesktop();
  const resolvedWidth = isDesktop ? desktopWidth : width;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      scroll="body"
      // Keep reward/challenge modals above desktop chrome (sidebar/header).
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 2,
        "& .MuiDialog-container": {
          alignItems: "center",
          p: { xs: 1.5, md: 3 },
        },
      }}
      PaperProps={{
        sx: {
          bgcolor: "transparent",
          boxShadow: "none",
          overflow: "visible",
          m: { xs: 1.5, md: 3 },
          width: "100%",
          maxWidth: `min(${resolvedWidth}px, calc(100vw - 24px))`,
        },
      }}
      slotProps={{
        backdrop: { sx: { bgcolor: "rgba(0,0,0,0.78)" } },
      }}
    >
      <Box sx={{ position: "relative", width: "100%" }}>
        {!hideClose && <GaCircleClose onClick={onClose} />}
        <Box
          sx={{
            bgcolor: ga.paper,
            clipPath: cutCorner(isDesktop ? 22 : 16),
            border: `1px solid ${ga.border}`,
            px: { xs: 2.5, md: 5 },
            pt: { xs: 3, md: 5 },
            pb: { xs: 2.5, md: 4 },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            maxHeight: { xs: "calc(100vh - 32px)", md: "calc(100vh - 48px)" },
            overflow: "auto",
            boxSizing: "border-box",
          }}
        >
          {children}
        </Box>
      </Box>
    </Dialog>
  );
}

export function GaLimeCta({
  children,
  onClick,
  disabled,
  loading,
  tone = "lime",
  type = "button",
  sx,
}) {
  const isDesktop = useIsDesktop();
  const bg = tone === "gold" ? ga.gold : ga.lime;
  return (
    <Box
      component="button"
      type={type}
      onClick={disabled || loading ? undefined : onClick}
      disabled={disabled || loading}
      sx={{
        width: isDesktop ? 220 : "100%",
        maxWidth: "100%",
        minHeight: isDesktop ? 48 : 44,
        border: 0,
        bgcolor: bg,
        color: ga.bg,
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        fontSize: isDesktop ? 14 : 13,
        letterSpacing: isDesktop ? "2px" : "1.4px",
        textTransform: "uppercase",
        clipPath: cutCorner(10),
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
        ...sx,
      }}
    >
      {loading ? <CircularProgress size={18} sx={{ color: ga.bg }} /> : children}
    </Box>
  );
}

export function GaGhostLink({ children, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        mt: 1.25,
        border: 0,
        bgcolor: "transparent",
        color: ga.dim,
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: "1.6px",
        cursor: "pointer",
      }}
    >
      {children}
    </Box>
  );
}

export function GaLimeChip({ children }) {
  return (
    <Box
      sx={{
        bgcolor: ga.lime,
        color: ga.bg,
        px: 1.5,
        py: 0.4,
        mb: 1.5,
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: "1.2px",
        clipPath: cutCorner(7),
      }}
    >
      {children}
    </Box>
  );
}

export function Eyebrow({ children }) {
  const isDesktop = useIsDesktop();
  return (
    <Typography
      sx={{
        fontFamily: "'Rajdhani', sans-serif",
        fontWeight: 700,
        fontSize: isDesktop ? 14 : 11,
        letterSpacing: isDesktop ? "3px" : "5px",
        color: ga.lime,
        mb: isDesktop ? 1.5 : 1.25,
      }}
    >
      {children}
    </Typography>
  );
}

export function Body({ children, sx }) {
  const isDesktop = useIsDesktop();
  return (
    <Typography
      sx={{
        fontFamily: "'Poppins', sans-serif",
        fontSize: isDesktop ? 14 : 11.5,
        lineHeight: isDesktop ? "21px" : "17px",
        color: ga.muted,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}

export function RedeemConfirmDialog({ open, prize, eggs = 0, phone, onClose, onConfirm }) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  if (!prize) return null;
  const enough = eggs >= prize.cost;
  const maskedPhone = maskJazzNumber(phone);
  return (
    <GaDialogShell open={open} onClose={onClose}>
      <Eyebrow>{d.redeem}</Eyebrow>
      <Box
        sx={{
          width: 72,
          height: 72,
          mb: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component="img"
          src={prize.image}
          alt={prize.title}
          sx={{ maxWidth: 64, maxHeight: 64, objectFit: "contain" }}
        />
      </Box>
      <Typography
        sx={{
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: 700,
          fontSize: 22,
          letterSpacing: "1px",
          color: ga.gold,
          lineHeight: "28px",
        }}
      >
        {prize.title}
      </Typography>
      <Body sx={{ mt: 0.25, mb: 1.5 }}>{prize.game}</Body>
      <Box
        sx={{
          width: "100%",
          border: `1px solid ${ga.gold}`,
          py: 0.85,
          mb: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.75,
        }}
      >
        <Box component="img" src={eggImg} alt="" sx={{ width: 11, height: 14 }} />
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontWeight: 600,
            fontSize: 12,
            color: ga.gold,
          }}
        >
          {fillCopy(d.eggsSpent, { n: prize.cost })}
        </Typography>
      </Box>
      <Body sx={{ mb: 2, fontSize: 12, color: "#8E8E8E" }}>
        {fillCopy(d.jazzWithin, { phone: maskedPhone })}
      </Body>
      {!enough && (
        <Body sx={{ color: ga.gold, mb: 1.5 }}>{d.insufficientEggs}</Body>
      )}
      <GaLimeCta onClick={onConfirm} disabled={!enough} sx={{ width: "100%" }}>
        {d.confirmRedeem}
      </GaLimeCta>
      <GaGhostLink onClick={onClose}>{d.notNow}</GaGhostLink>
    </GaDialogShell>
  );
}

export function RedeemSuccessDialog({ open, prize, onClose }) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  if (!prize) return null;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      elevation={0}
      PaperProps={{
        elevation: 0,
        sx: {
          bgcolor: "#141910",
          backgroundImage: "none",
          boxShadow: "none",
          overflow: "hidden",
          m: "78px 18px 28px",
          width: { xs: "min(360px, calc(100vw - 36px))", md: 440 },
          maxWidth: { xs: "calc(100vw - 36px)", md: 440 },
          height: { xs: "calc(100vh - 150px)", md: "auto" },
          maxHeight: { xs: "calc(100vh - 106px)", md: "none" },
          borderRadius: 0,
          border: 0,
          clipPath: cutCorner(22),
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2.75,
          py: 4,
          boxSizing: "border-box",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0,0,0,0.9)",
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
          },
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0,0,0,0.9)",
          backdropFilter: "none",
        },
        "& .MuiDialog-container": {
          alignItems: "flex-start",
          backgroundColor: "transparent",
        },
      }}
    >
      <Box
        sx={{
          width: 74,
          height: 74,
          borderRadius: "50%",
          border: "2px solid #B8EE3C",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2.75,
          flexShrink: 0,
        }}
      >
        <Box component="svg" viewBox="0 0 32 32" aria-hidden="true" sx={{ width: 32, height: 32, display: "block" }}>
          <path
            d="M7 16.8 L13.2 23 L25.2 10"
            fill="none"
            stroke="#B8EE3C"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </Box>
      </Box>
      <Typography
        sx={{
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: 700,
          fontSize: 13,
          letterSpacing: "1.8px",
          color: ga.lime,
          mb: 1.25,
        }}
      >
        {d.requestReceived}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: 700,
          fontSize: 32,
          lineHeight: 1,
          letterSpacing: "0.6px",
          color: ga.white,
          mb: 2,
        }}
      >
        {d.thankYou}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: 13.5,
          lineHeight: 1.45,
          color: "#E7EBDF",
          maxWidth: 300,
          mb: 1.75,
        }}
      >
        {d.requestReceivedBody}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: 12.5,
          lineHeight: 1.5,
          color: "#C5CBBE",
          maxWidth: 300,
          mb: 2.25,
        }}
      >
        {d.businessHours}
      </Typography>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 700,
          fontSize: 15,
          color: ga.lime,
          mb: 3.5,
        }}
      >
        {d.happyGaming}
      </Typography>
      <Box
        component="button"
        type="button"
        onClick={onClose}
        sx={{
          width: "100%",
          maxWidth: 320,
          minHeight: 48,
          border: 0,
          bgcolor: ga.lime,
          color: ga.bg,
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: 700,
          fontSize: 16,
          letterSpacing: "1.2px",
          clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%)",
          cursor: "pointer",
        }}
      >
        {d.backToRewards}
      </Box>
    </Dialog>
  );
}

export function RewardFreeTrialDialog({ open, onClose }) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  return (
    <GaDialogShell open={open} onClose={onClose}>
      <Box
        sx={{
          width: "100%",
          bgcolor: "#3A1512",
          border: "1px solid #E85D4C",
          p: 1.5,
          mb: 2,
          textAlign: "left",
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: "2px",
            color: "#E85D4C",
            mb: 0.75,
          }}
        >
          {d.freeTrial}
        </Typography>
        <Body sx={{ color: "#F2C4BE", textAlign: "left" }}>{d.freeTrialBody}</Body>
      </Box>
      <GaLimeCta onClick={onClose} sx={{ width: "100%" }}>
        {d.ok}
      </GaLimeCta>
    </GaDialogShell>
  );
}

export function NotEnoughEggsDialog({ open, cost = 0, onClose, onEarn }) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  const needed = Math.max(0, Number(cost) || 0);
  const lastCost = useRef(needed);
  if (needed > 0) lastCost.current = needed;
  const displayCost = needed > 0 ? needed : lastCost.current;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      elevation={0}
      PaperProps={{
        elevation: 0,
        sx: {
          bgcolor: "#141910",
          backgroundImage: "none",
          boxShadow: "none",
          overflow: "hidden",
          position: "relative",
          m: "24px 28px",
          width: "min(334px, calc(100vw - 56px))",
          maxWidth: "calc(100vw - 56px)",
          height: "auto",
          borderRadius: 0,
          border: 0,
          display: "flex",
          flexDirection: "column",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0,0,0,0.9)",
            backdropFilter: "none",
            WebkitBackdropFilter: "none",
          },
        },
      }}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(0,0,0,0.9)",
          backdropFilter: "none",
        },
        "& .MuiDialog-container": {
          alignItems: "center",
          backgroundColor: "transparent",
        },
      }}
    >
      <GaCircleClose onClick={onClose} />
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          px: 2.5,
          pt: 4.5,
          pb: 2.75,
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 74,
            height: 74,
            borderRadius: "50%",
            border: "2px solid #B8EE3C",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <Box component="svg" viewBox="0 0 24 24" aria-hidden="true" sx={{ width: 28, height: 28, display: "block" }}>
            <rect x="5" y="11" width="14" height="10" rx="1.6" fill="none" stroke="#B8EE3C" strokeWidth="1.7" />
            <path d="M8 11V8.2a4 4 0 0 1 8 0V11" fill="none" stroke="#B8EE3C" strokeWidth="1.7" strokeLinecap="round" />
            <circle cx="12" cy="16" r="1.15" fill="#B8EE3C" />
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: "1.6px",
            color: ga.lime,
            mb: 0.75,
          }}
        >
          {d.cantRedeemYet}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 26,
            lineHeight: 1,
            letterSpacing: "0.4px",
            color: ga.white,
            mb: 1.5,
          }}
        >
          {d.notEnough}{" "}
          <Box component="span" sx={{ color: ga.lime }}>
            {d.notEnoughEggs}
          </Box>
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Poppins', sans-serif",
            fontSize: 13,
            lineHeight: 1.45,
            color: "#E7EBDF",
            maxWidth: 280,
            mb: 1.75,
          }}
        >
          {d.notEnoughBody}
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: "0.6px",
            color: "#F2F5EC",
            mb: 2.25,
          }}
        >
          {d.minimumNeeded}
          {" · "}
          <Box component="span" sx={{ color: ga.lime }}>
            {displayCost} {d.eggsUnit}
          </Box>
        </Typography>
        <Box
          component="button"
          type="button"
          onClick={onEarn}
          sx={{
            width: "100%",
            minHeight: 46,
            border: 0,
            bgcolor: ga.lime,
            color: ga.bg,
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 16,
            letterSpacing: "1.2px",
            cursor: "pointer",
          }}
        >
          {d.earnMoreEggs}
        </Box>
      </Box>
    </Dialog>
  );
}

export function DailyStreakDialog({
  open,
  claimed = false,
  streakDay = 1,
  claimAmount = 1,
  claiming = false,
  onClose,
  onClaim,
  onPlay,
}) {
  const { t } = useUiLangState();
  const isDesktop = useIsDesktop();
  const d = t.dialogs;
  const days = useMemo(
    () =>
      [1, 2, 3, 4, 5, 6, 7].map((day) => ({
        day,
        state: day < streakDay ? "done" : day === streakDay ? "today" : "locked",
      })),
    [streakDay]
  );
  const nextDay = Math.min(7, streakDay + 1);

  return (
    <GaDialogShell
      open={open}
      onClose={onClose}
      desktopWidth={claimed ? 560 : 600}
    >
      <Eyebrow>{d.dailyStreak}</Eyebrow>
      {claimed ? (
        <>
          <Box
            component="img"
            src={eggImg}
            alt=""
            sx={{
              width: isDesktop ? 84 : 56,
              height: isDesktop ? 114 : 72,
              objectFit: "contain",
              mb: isDesktop ? 2.5 : 1.5,
            }}
          />
          <Typography
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: isDesktop ? 24 : 16,
              letterSpacing: "1px",
              color: ga.text,
              mb: 1,
              px: 1,
            }}
          >
            {d.alreadyClaimed}
          </Typography>
          <Body sx={{ mb: isDesktop ? 4 : 2, maxWidth: isDesktop ? 460 : 280 }}>
            {fillCopy(d.alreadyClaimedBody, { n: nextDay })}
          </Body>
          <GaLimeCta onClick={onPlay} sx={{ width: isDesktop ? 260 : "100%" }}>
            {d.playChallenges}
          </GaLimeCta>
        </>
      ) : (
        <>
          {isDesktop ? (
            <>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: 30,
                  color: ga.text,
                  mb: 1,
                }}
              >
                {d.streakAliveTitle}
              </Typography>
              <Body sx={{ mb: 3 }}>{d.streakAlive}</Body>
            </>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1.5 }}>
              <Box component="img" src={icFireTitle} alt="" sx={{ width: 19, height: 19 }} />
              <Typography
                sx={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 700,
                  fontSize: 22,
                  letterSpacing: "1.5px",
                  color: ga.white,
                }}
              >
                {d.day}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 700,
                  fontSize: 22,
                  letterSpacing: "1.5px",
                  color: ga.lime,
                }}
              >
                {streakDay}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              gap: isDesktop ? "8px" : "6px",
              justifyContent: "center",
              width: "100%",
              mb: isDesktop ? 2.5 : 1.5,
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              pb: 0,
            }}
          >
            {days.map((item) => (
              <Box
                key={item.day}
                sx={{
                  width: isDesktop ? "auto" : 34,
                  flex: isDesktop ? "1 1 0" : "none",
                  minWidth: isDesktop ? 0 : 34,
                  maxWidth: isDesktop ? 68 : 34,
                  height: isDesktop ? 86 : 42,
                  flexShrink: isDesktop ? 1 : 0,
                  bgcolor: ga.bg,
                  border: `1px solid ${
                    item.state === "today"
                      ? ga.lime
                      : item.state === "done"
                        ? ga.gold
                        : ga.border
                  }`,
                  clipPath: isDesktop ? cutCorner(8) : "none",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: isDesktop ? "6px" : "2px",
                  opacity: item.state === "locked" ? 0.55 : 1,
                }}
              >
                {isDesktop ? (
                  <>
                    <Typography
                      sx={{
                        fontFamily: "'Rajdhani', sans-serif",
                        fontWeight: 700,
                        fontSize: 10,
                        letterSpacing: "1px",
                        color: item.state === "today" ? ga.lime : ga.muted,
                      }}
                    >
                      {d.day} {item.day}
                    </Typography>
                    <Box
                      component="img"
                      src={eggImg}
                      alt=""
                      sx={{ width: 22, height: 30, objectFit: "contain" }}
                    />
                  </>
                ) : item.state === "locked" ? (
                  <Typography sx={{ color: ga.dim, fontSize: 13, lineHeight: "20px" }}>
                    ·
                  </Typography>
                ) : (
                  <>
                    <Box component="img" src={icFireSmall} alt="" sx={{ width: 11, height: 11 }} />
                    <Typography
                      sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: 7.5,
                        color: ga.dim,
                        lineHeight: "11px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {d.day} {item.day}
                    </Typography>
                  </>
                )}
              </Box>
            ))}
          </Box>
          {isDesktop ? (
            <>
              <Typography
                sx={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 700,
                  fontSize: 64,
                  color: ga.text,
                  lineHeight: 1,
                }}
              >
                {streakDay}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontWeight: 700,
                  fontSize: 13,
                  letterSpacing: "2px",
                  color: ga.muted,
                  mb: 3,
                }}
              >
                {d.dayStreak}
              </Typography>
            </>
          ) : (
            <Body sx={{ mb: 2 }}>{d.streakAlive}</Body>
          )}
          <GaLimeCta onClick={onClaim} loading={claiming}>
            {`CLAIM +${claimAmount}`}
            <Box component="img" src={claimEgg} alt="" sx={{ width: 15, height: 18 }} />
          </GaLimeCta>
        </>
      )}
    </GaDialogShell>
  );
}

export function StreakClaimModal({
  open,
  onClose,
  streakCount,
  streakDays,
  claimedToday,
  claimDay,
  claimAmount = 1,
  claiming = false,
  onClaim,
  onPlayChallenges,
}) {
  const headerDay = claimedToday
    ? Math.max(1, streakCount || 1)
    : Math.max(1, (streakCount || 0) + 1, claimDay || 1);

  return (
    <GaDialogShell open={open} onClose={onClose} width={380}>
      <Typography
        sx={{
          color: ga.lime,
          fontFamily: "'Rajdhani', sans-serif",
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: "4px",
          mb: 1.5,
        }}
      >
        DAILY STREAK
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 3 }}>
        <Box component="img" src={fireIcon} alt="streak" sx={{ width: 24, height: 24 }} />
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 32,
            letterSpacing: "2px",
            color: ga.white,
            lineHeight: 1
          }}
        >
          DAY <Box component="span" sx={{ color: ga.lime }}>{headerDay}</Box>
        </Typography>
      </Box>

      <Box
        sx={{
          bgcolor: claimedToday ? 'transparent' : ga.lime,
          border: claimedToday ? `1px solid ${ga.lime}` : 'none',
          py: 1.5,
          px: 2,
          mb: 3,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1.5,
          cursor: claimedToday || claiming ? 'default' : 'pointer',
          opacity: claiming ? 0.6 : 1,
        }}
        onClick={claimedToday || claiming ? undefined : onClaim}
      >
        {claimedToday ? (
          <>
            <Typography sx={{ color: ga.lime, fontSize: 16 }}>✓</Typography>
            <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 14, letterSpacing: '2px', color: ga.lime }}>
              TODAY'S EGG ALREADY CLAIMED
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: 16, letterSpacing: '2px', color: ga.bg }}>
            {claiming ? "CLAIMING" : `CLAIM +${claimAmount}`}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: '6px', mb: 4, width: '100%' }}>
        {streakDays.map((item) => {
          const isToday = Boolean(item.current);
          const isPast = Boolean(item.claimed) && !isToday;
          const isFuture = Boolean(item.blank) || item.state === "locked";

          let borderColor = ga.border;
          if (isToday && !claimedToday) borderColor = ga.lime;
          else if (isPast || (isToday && claimedToday)) borderColor = ga.gold;

          return (
            <Box
              key={item.dateKey || item.day}
              sx={{
                width: 42,
                height: 52,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: ga.bg,
                border: `1px solid ${borderColor}`,
              }}
            >
              {isFuture ? (
                <Typography sx={{ color: ga.dim, fontSize: 14, lineHeight: 1 }}>·</Typography>
              ) : (
                <Box component="img" src={fireIcon} alt="" sx={{ width: 14, height: 14, mb: 0.25 }} />
              )}
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 9,
                  color: ga.dim,
                  mt: 0.5
                }}
              >
                DAY {item.day}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <GaLimeCta onClick={onPlayChallenges} sx={{ width: '100%', mb: 2 }}>
        PLAY TODAY'S CHALLENGES
      </GaLimeCta>
      
      <GaGhostLink onClick={onClose} sx={{ fontSize: 14 }}>
        LATER
      </GaGhostLink>
    </GaDialogShell>
  );
}

export { EggCollectedDialog } from './EggCollectedDialog';

export function RateContentDialog({
  open,
  onClose,
  rating,
  onRate,
  comment,
  onComment,
  onSubmit,
  submitting,
  error,
}) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  return (
    <GaDialogShell open={open} onClose={onClose}>
      <GaLimeChip>{d.rateChip}</GaLimeChip>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontWeight: 700,
          fontSize: 15,
          color: ga.white,
          mb: 1.5,
        }}
      >
        {d.ratePrompt}
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.75 }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <IconButton
            key={star}
            onClick={() => onRate(star)}
            sx={{ color: star <= rating ? ga.gold : "#3A3F30", p: 0.4 }}
          >
            {star <= rating ? (
              <StarIcon sx={{ fontSize: 32 }} />
            ) : (
              <StarBorderIcon sx={{ fontSize: 32 }} />
            )}
          </IconButton>
        ))}
      </Box>
      <Box sx={{ width: "100%", mb: 1.25, display: "flex", alignItems: "center", gap: 1 }}>
        <Box component="img" src={icComment} alt="" sx={{ width: 14, height: 14 }} />
        <TextField
          fullWidth
          variant="standard"
          placeholder={d.commentOptional}
          value={comment}
          onChange={(event) => onComment(event.target.value)}
          InputProps={{ disableUnderline: true }}
          sx={{
            "& input": {
              color: ga.muted,
              fontFamily: "'Poppins', sans-serif",
              fontSize: 12,
            },
          }}
        />
      </Box>
      {error && (
        <Body sx={{ color: "#E85D4C", mb: 1 }}>{error}</Body>
      )}
      <GaLimeCta onClick={onSubmit} disabled={!rating} loading={submitting}>
        {d.submit}
      </GaLimeCta>
    </GaDialogShell>
  );
}

export { PuzzleBoardDialog } from './PuzzleBoardDialog';

export function rewardChoiceLabel(prize) {
  const title = String(prize?.title || "").trim();
  const hay = `${title} ${prize?.game || ""} ${prize?.gameKey || ""}`.toLowerCase();
  const amount = title.match(/(\d+)/);
  if (hay.includes("diamond") || hay.includes("free fire") || hay.includes("freefire")) {
    return amount ? `${amount[1]} Diamonds` : title;
  }
  if (hay.includes("uc") || hay.includes("pubg")) {
    return amount ? `${amount[1]} UC` : title;
  }
  return title;
}

export function RewardChoiceDialog({ open, prizes = [], eggs = 0, onClose, onSelect }) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  const isDesktop = useIsDesktop();
  const choices = prizes.filter((prize) => prize && !prize.freeTrial);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      elevation={0}
      PaperProps={{
        elevation: 0,
        sx: {
          bgcolor: "#0C0F0A",
          backgroundImage: "none",
          boxShadow: "none",
          overflow: "visible",
          position: "relative",
          mt: isDesktop ? 0 : "136px",
          mb: isDesktop ? 0 : "74px",
          mx: isDesktop ? "auto" : "12px",
          width: {
            xs: "min(390px, calc(100vw - 24px))",
            md: "min(360px, calc(100vw - 280px))",
            lg: "min(400px, calc(100vw - 300px))",
            xl: "min(440px, calc(100vw - 320px))",
          },
          maxWidth: {
            xs: "calc(100vw - 24px)",
            md: 360,
            lg: 400,
            xl: 440,
          },
          height: {
            xs: "calc(100vh - 210px)",
            md: "min(620px, calc(100vh - 100px))",
            lg: "min(680px, calc(100vh - 90px))",
            xl: "min(740px, calc(100vh - 80px))",
          },
          maxHeight: {
            xs: "calc(100vh - 210px)",
            md: "min(620px, calc(100vh - 100px))",
            lg: "min(680px, calc(100vh - 90px))",
            xl: "min(740px, calc(100vh - 80px))",
          },
          borderRadius: 0,
          border: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            bgcolor: "rgba(0,0,0,0.68)",
            backdropFilter: "blur(3px)",
            WebkitBackdropFilter: "blur(3px)",
          },
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: isDesktop ? "center" : "flex-start",
        },
      }}
    >
      <GaCircleClose onClick={onClose} />

      <Box
        sx={{
          width: "100%",
          px: { xs: 2.5, md: 2.25, lg: 2.75, xl: 3 },
          textAlign: "center",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
          <Typography
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: { xs: 11, md: 11, lg: 12, xl: 13 },
              letterSpacing: "2.2px",
              color: ga.lime,
              mb: { xs: 1, md: 1, lg: 1.25 },
            }}
          >
            {d.redeemYourReward}
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Rajdhani', sans-serif",
              fontWeight: 700,
              fontSize: { xs: 26, md: 24, lg: 28, xl: 32 },
              letterSpacing: "0.2px",
              lineHeight: 1,
              color: ga.white,
              textTransform: "uppercase",
              mb: 0.75,
            }}
          >
            {d.chooseRewardLead}{" "}
            <Box component="span" sx={{ color: ga.lime }}>
              {d.chooseRewardAccent}
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Poppins', sans-serif",
              fontSize: { xs: 12, md: 12, lg: 13, xl: 14 },
              color: "#9AA392",
              mb: { xs: 2.5, md: 2.25, lg: 2.75, xl: 3 },
            }}
          >
            {d.pickCurrency}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: choices.length > 1 ? "1fr 1fr" : "1fr",
              gap: { xs: 1.25, md: 1.25, lg: 1.5, xl: 1.75 },
              maxWidth: { xs: 300, md: 260, lg: 300, xl: 320 },
              width: "100%",
              mx: "auto",
              justifyContent: "center",
              justifyItems: "center",
            }}
          >
            {choices.map((prize) => {
              const short = eggs < Number(prize?.cost || 0);
              const affordable =
                Boolean(prize?.canRedeem && prize?.available) && !short;
              const tappable = short || affordable;
              return (
                <Box
                  key={prize.id}
                  component="button"
                  type="button"
                  disabled={!tappable}
                  onClick={() => tappable && onSelect(prize)}
                  sx={{
                    width: "100%",
                    minHeight: { xs: 148, md: 132, lg: 150, xl: 168 },
                    px: { xs: 1, md: 1, lg: 1.25 },
                    py: { xs: 1.75, md: 1.5, lg: 1.75, xl: 2 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.35,
                    bgcolor: "#0E120C",
                    border: "1px solid #2C3424",
                    borderRadius: 0,
                    color: ga.white,
                    cursor: tappable ? "pointer" : "default",
                    "&:hover": tappable ? { borderColor: ga.lime } : undefined,
                  }}
                >
                  {prize.image ? (
                    <Box
                      component="img"
                      src={prize.image}
                      alt=""
                      sx={{
                        width: { xs: 46, md: 42, lg: 48, xl: 54 },
                        height: { xs: 46, md: 42, lg: 48, xl: 54 },
                        objectFit: "contain",
                        mb: 0.75,
                      }}
                    />
                  ) : null}
                  <Typography
                    sx={{
                      fontFamily: "'Poppins', sans-serif",
                      fontWeight: 700,
                      fontSize: { xs: 15, md: 14, lg: 15, xl: 16 },
                      color: ga.white,
                      lineHeight: 1.2,
                    }}
                  >
                    {rewardChoiceLabel(prize)}
                  </Typography>
                  {prize.game ? (
                    <Typography
                      sx={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: { xs: 11, md: 10, lg: 11, xl: 12 },
                        color: ga.muted,
                        lineHeight: 1.2,
                      }}
                    >
                      {prize.game}
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
          </Box>
      </Box>
    </Dialog>
  );
}

export function ChooseRewardDialog({ open, games = [], onClose, onSelect }) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  return (
    <GaDialogShell open={open} onClose={onClose}>
      <Eyebrow>{d.chooseReward}</Eyebrow>
      <Body sx={{ mb: 1.5 }}>{d.chooseRewardBody}</Body>
      <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 1 }}>
        {games.map((game, index) => (
          <Box
            key={game.name}
            onClick={() => onSelect(index)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              p: 1,
              bgcolor: ga.bg,
              border: `1px solid ${ga.border}`,
              cursor: "pointer",
              textAlign: "left",
              "&:hover": { borderColor: ga.lime },
            }}
          >
            <Box
              component="img"
              src={game.thumbnail}
              alt=""
              sx={{ width: 56, height: 44, objectFit: "cover" }}
            />
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                fontSize: 13,
                color: ga.text,
              }}
            >
              {game.name}
            </Typography>
          </Box>
        ))}
      </Box>
    </GaDialogShell>
  );
}

export function ComeTomorrowDialog({ open, onClose, onPlay }) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  return (
    <GaDialogShell open={open} onClose={onClose}>
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          border: `2px solid ${ga.lime}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2.5,
        }}
      >
        <Box component="img" src={icLock} alt="" sx={{ width: 32, height: 32 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, justifyContent: 'center' }}>
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: "1px",
            color: ga.white,
            textTransform: "uppercase"
          }}
        >
          NEW PUZZLE
        </Typography>
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: "1px",
            color: ga.lime,
            textTransform: "uppercase"
          }}
        >
          TOMORROW
        </Typography>
      </Box>
      <Typography 
        sx={{ 
          fontFamily: "'Poppins', sans-serif",
          fontSize: 13,
          color: ga.muted,
          mb: 2.5,
          textAlign: "center",
          lineHeight: 1.4
        }}
      >
        A new puzzle unlocks at 12 AM every night<br />come back tomorrow
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, justifyContent: 'center' }}>
        <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: 'white', fontWeight: 700, letterSpacing: '1px' }}>
          NEXT PUZZLE
        </Typography>
        <Typography sx={{ color: ga.muted, fontSize: 13 }}>·</Typography>
        <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: ga.gold, fontWeight: 700, letterSpacing: '1px' }}>
          12:00 AM
        </Typography>
      </Box>
      
      <GaLimeCta onClick={onPlay}>{d.playTodaysPuzzles || "PLAY TODAY'S PUZZLES"}</GaLimeCta>
    </GaDialogShell>
  );
}

export function PuzzleTrialDialog({ open, puzzleName, onClose, onRedeem }) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  return (
    <GaDialogShell open={open} onClose={onClose}>
      <GaLimeChip>{puzzleName || d.battlefield}</GaLimeChip>
      <Body sx={{ mb: 1.5 }}>{d.clickPiece}</Body>
      <Box
        sx={{
          width: "100%",
          bgcolor: "#3A1512",
          border: "1px solid #E85D4C",
          p: 1.5,
          mb: 1.5,
          textAlign: "left",
        }}
      >
        <Typography
          sx={{
            fontFamily: "'Rajdhani', sans-serif",
            fontWeight: 700,
            fontSize: 12,
            letterSpacing: "2px",
            color: "#E85D4C",
            mb: 0.75,
          }}
        >
          {d.freeTrial}
        </Typography>
        <Body sx={{ color: "#F2C4BE", textAlign: "left" }}>{d.freeTrialBody}</Body>
      </Box>
      <GaLimeCta tone="gold" onClick={onRedeem}>
        {d.redeem}
      </GaLimeCta>
      <Body sx={{ mt: 1.5 }}>{d.unlockHint}</Body>
    </GaDialogShell>
  );
}

export { PuzzleRewardDialog } from './PuzzleRewardDialog';

export function EasterCodeFoundDialog({ open, code = "", onCopy, copied, onClose }) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  return (
    <GaDialogShell open={open} onClose={onClose}>
      <GaLimeChip>{d.easterFound}</GaLimeChip>
      <Body sx={{ mb: 2 }}>{d.easterFoundBody}</Body>
      <Box
        sx={{
          width: "100%",
          bgcolor: ga.bg,
          border: `1px solid ${ga.lime}`,
          py: 1.25,
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontFamily: "monospace",
            fontSize: 22,
            letterSpacing: "0.28em",
            color: ga.lime,
            fontWeight: 700,
          }}
        >
          {code}
        </Typography>
      </Box>
      <GaLimeCta onClick={onCopy}>{copied ? d.copied : d.copyCode}</GaLimeCta>
    </GaDialogShell>
  );
}

export function PuzzlePhoneDialog({
  open,
  onClose,
  phoneNumber,
  onChange,
  phoneError,
  onSubmit,
  submitting,
}) {
  const { t } = useUiLangState();
  const d = t.dialogs;
  return (
    <GaDialogShell open={open} onClose={onClose}>
      <Eyebrow>{d.jazzNumber}</Eyebrow>
      <Body sx={{ mb: 1.5 }}>{d.jazzNumberBody}</Body>
      <Box
        component="input"
        type="tel"
        maxLength={11}
        value={phoneNumber}
        onChange={onChange}
        placeholder="03XXXXXXXXX"
        sx={{
          width: "100%",
          bgcolor: ga.bg,
          border: `1px solid ${ga.border}`,
          color: ga.white,
          fontFamily: "'Poppins', sans-serif",
          fontSize: 16,
          p: 1.25,
          mb: 1,
          outline: "none",
        }}
      />
      {phoneError && (
        <Body sx={{ color: "#E85D4C", mb: 1 }}>{phoneError}</Body>
      )}
      <GaLimeCta onClick={onSubmit} loading={submitting}>
        {d.submit}
      </GaLimeCta>
      <GaGhostLink onClick={onClose}>{d.notNow}</GaGhostLink>
    </GaDialogShell>
  );
}

export function getDefaultNextPrize(eggs) {
  const ladder = [
    { title: "60 UC", cost: 240 },
    { title: "325 UC", cost: 380 },
    { title: "100 DIAMONDS", cost: 280 },
    { title: "200 DIAMONDS", cost: 480 },
    { title: "330 MINECOINS", cost: 320 },
  ];
  const next = ladder.find((item) => eggs < item.cost) || ladder[ladder.length - 1];
  return {
    title: next.title,
    moreNeeded: Math.max(0, next.cost - eggs),
  };
}

export function getTodayKey() {
  return new Date().toISOString().slice(0, 10);
}

export function getHomeStreakState() {
  const today = getTodayKey();
  let profile = null;
  try {
    profile = JSON.parse(sessionStorage.getItem("profileData") || "null");
  } catch {
    profile = null;
  }
  return {
    today,
    streakDay: Math.min(7, Math.max(1, getStreakCount(profile) || 1)),
    shown: sessionStorage.getItem(`ga_streak_shown_${today}`) === "1",
    claimed: sessionStorage.getItem(`ga_streak_claimed_${today}`) === "1",
  };
}

export function markStreakShown(today = getTodayKey(), claimed = false) {
  sessionStorage.setItem(`ga_streak_shown_${today}`, "1");
  if (claimed) sessionStorage.setItem(`ga_streak_claimed_${today}`, "1");
}

export function saveRedemptionHistory(entry) {
  let history = [];
  try {
    const parsed = JSON.parse(sessionStorage.getItem("redemptionHistory") || "[]");
    history = Array.isArray(parsed) ? parsed : [];
  } catch {
    history = [];
  }
  const next = [entry, ...history].slice(0, 20);
  sessionStorage.setItem("redemptionHistory", JSON.stringify(next));
  return next;
}
